import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminBoxEditDialog } from "@/components/AdminBoxEditDialog";
import { UserLabelsSection } from "@/components/UserLabelsSection";
import {
  ArrowLeft, ExternalLink, Mail, Package, DollarSign, Calendar,
  MapPin, User as UserIcon, Copy, Eye, Pencil, Save, Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface UserData {
  id: string;
  email: string;
  full_name: string | null;
  phone: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  address_line1: string | null;
  address_line2?: string | null;
  created_at: string;
  signup_source: string | null;
  use_case: string | null;
  billing_start_date: string | null;
  box_slots_purchased?: number | null;
  box_slots_used?: number | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  total_boxes: number;
  total_items: number;
  monthly_revenue: number;
  months_stored: number | null;
  status: "active" | "inactive" | "never_stored";
  stored_boxes: number;
}

interface Box {
  id: string;
  box_number: string;
  name: string;
  location_status: string;
  storage_start_date: string | null;
  stored_at: string | null;
  created_at: string;
  shipping_label_url: string | null;
  tracking_number: string | null;
  easypost_tracking_url: string | null;
  label_generated_at: string | null;
}

// ─── Skeleton components for progressive loading ───

const HeaderSkeleton = () => (
  <div className="flex items-center justify-between mb-8">
    <div>
      <Skeleton className="h-9 w-64 mb-2" />
      <Skeleton className="h-4 w-40" />
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-8 w-20" />
      <Skeleton className="h-8 w-28" />
      <Skeleton className="h-8 w-28" />
    </div>
  </div>
);

const CardSkeleton = ({ className = "" }: { className?: string }) => (
  <Card className={className}>
    <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
    <CardContent className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
      </div>
      <Skeleton className="h-12" />
    </CardContent>
  </Card>
);

const BoxListSkeleton = () => (
  <Card className="mt-6">
    <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
    <CardContent className="space-y-2">
      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
    </CardContent>
  </Card>
);

// ─── Main component ───

const AdminUserDetail = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [user, setUser] = useState<UserData | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [loadingBoxes, setLoadingBoxes] = useState(true);
  const [editingBox, setEditingBox] = useState<Box | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Profile editing state
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState("");
  const [boxSlotsPurchased, setBoxSlotsPurchased] = useState("");
  const [billingStartDate, setBillingStartDate] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [hasProfileChanges, setHasProfileChanges] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setLoadingUser(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/auth"); return; }

      const { data, error } = await supabase.functions.invoke("admin-get-user", {
        body: { user_id: userId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;
      if (!data || data.error) {
        toast({ title: "User not found", variant: "destructive" });
        navigate("/admin/users");
        return;
      }

      setUser(data as UserData);
      setStripeCustomerId(data.stripe_customer_id || "");
      setStripeSubscriptionId(data.stripe_subscription_id || "");
      setBoxSlotsPurchased(data.box_slots_purchased?.toString() || "");
      setBillingStartDate(data.billing_start_date || "");
    } catch (error: any) {
      console.error("Error fetching user:", error);
      toast({ title: "Error loading user", description: error.message, variant: "destructive" });
    } finally {
      setLoadingUser(false);
    }
  }, [userId, navigate, toast]);

  const fetchUserBoxes = useCallback(async () => {
    if (!userId) return;
    setLoadingBoxes(true);
    try {
      const { data, error } = await supabase
        .from("boxes")
        .select("id, box_number, name, location_status, storage_start_date, stored_at, created_at, shipping_label_url, tracking_number, easypost_tracking_url, label_generated_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setBoxes(data || []);
    } catch (error) {
      console.error("Error fetching boxes:", error);
    } finally {
      setLoadingBoxes(false);
    }
  }, [userId]);

  // Fetch user and boxes in parallel
  useEffect(() => {
    fetchUser();
    fetchUserBoxes();
  }, [fetchUser, fetchUserBoxes]);

  // Track profile changes
  useEffect(() => {
    if (user) {
      const changed =
        stripeCustomerId !== (user.stripe_customer_id || "") ||
        stripeSubscriptionId !== (user.stripe_subscription_id || "") ||
        boxSlotsPurchased !== (user.box_slots_purchased?.toString() || "") ||
        billingStartDate !== (user.billing_start_date || "");
      setHasProfileChanges(changed);
    }
  }, [stripeCustomerId, stripeSubscriptionId, boxSlotsPurchased, billingStartDate, user]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: `${label} copied to clipboard` });
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke("admin-update-profile", {
        body: {
          profileId: user.id,
          updates: {
            stripe_customer_id: stripeCustomerId || null,
            stripe_subscription_id: stripeSubscriptionId || null,
            box_slots_purchased: boxSlotsPurchased ? parseInt(boxSlotsPurchased, 10) : null,
            billing_start_date: billingStartDate || null,
          },
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      toast({ title: "Saved", description: "Profile updated successfully" });
      setHasProfileChanges(false);
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to save", variant: "destructive" });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleImpersonate = async () => {
    if (!user) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke("impersonate-user", {
        body: { targetEmail: user.email, originUrl: window.location.origin },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;

      sessionStorage.setItem("admin_recovery_link", data.adminRecoveryLink);
      sessionStorage.setItem("impersonation_active", "true");
      sessionStorage.setItem("impersonation_target", user.email);
      toast({ title: "Impersonation starting", description: `Switching to ${user.email}'s session...` });
      window.location.href = data.actionLink;
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to start impersonation", variant: "destructive" });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active": return <Badge className="bg-green-500">Active</Badge>;
      case "inactive": return <Badge variant="secondary">Inactive</Badge>;
      case "never_stored": return <Badge variant="outline">Never Stored</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getLocationBadge = (status: string) => {
    switch (status) {
      case "stored": return <Badge>Stored</Badge>;
      case "in_transit": return <Badge variant="secondary">In Transit</Badge>;
      case "home": return <Badge variant="outline">At Home</Badge>;
      case "pickup_scheduled": return <Badge variant="secondary">Pickup Scheduled</Badge>;
      case "returning": return <Badge variant="secondary">Returning</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  return (
    <div className="container mx-auto py-8 px-4 max-w-5xl">
      <Button variant="ghost" onClick={() => navigate("/admin/users")} className="mb-6">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Users
      </Button>

      {/* Header — skeleton until user loads */}
      {loadingUser ? <HeaderSkeleton /> : user ? (
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <UserIcon className="h-7 w-7" />
              {user.full_name || "User Details"}
            </h1>
            <p className="text-muted-foreground mt-1">{user.email}</p>
          </div>
          <div className="flex gap-2">
            {getStatusBadge(user.status)}
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(user.email, "Email")}>
              <Copy className="h-4 w-4 mr-1" /> Copy Email
            </Button>
            <Button size="sm" variant="outline" onClick={handleImpersonate}>
              <Eye className="h-4 w-4 mr-1" /> Impersonate
            </Button>
            {stripeCustomerId && (
              <Button size="sm" variant="outline" onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripeCustomerId}`, "_blank")}>
                <ExternalLink className="h-4 w-4 mr-1" /> Stripe
              </Button>
            )}
          </div>
        </div>
      ) : null}

      {/* Profile + Metrics — skeleton until user loads, boxes section loads independently */}
      <div className="grid gap-6 md:grid-cols-2">
        {loadingUser ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : user ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UserIcon className="h-4 w-4" /> Profile Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <p className="font-medium">{user.phone}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Signup Date:</span>
                    <p className="font-medium">{format(new Date(user.created_at), "MMM d, yyyy")}</p>
                  </div>
                </div>
                {user.use_case && (
                  <div>
                    <span className="text-muted-foreground">Use Case:</span>
                    <p className="font-medium">{user.use_case}</p>
                  </div>
                )}
                {user.address_line1 && (
                  <>
                    <Separator />
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1 mb-1">
                        <MapPin className="h-3 w-3" /> Address
                      </span>
                      <p>{user.address_line1}</p>
                      {user.address_line2 && <p>{user.address_line2}</p>}
                      <p>{user.city && `${user.city}, `}{user.state} {user.zip}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="h-4 w-4" /> Storage Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Boxes:</span>
                    <p className="font-medium text-lg">{user.total_boxes}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">In Storage:</span>
                    <p className="font-medium text-lg">{user.stored_boxes}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Items:</span>
                    <p className="font-medium text-lg">{user.total_items}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Box Slots:</span>
                    <p className="font-medium text-lg">{user.box_slots_used || 0} / {user.box_slots_purchased || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {/* Labels & Shipping — loads independently based on boxes */}
      <Card className="mt-6">
        <CardContent className="pt-6">
          {loadingBoxes || loadingUser ? (
            <div className="space-y-3">
              <Skeleton className="h-6 w-40" />
              <div className="grid grid-cols-2 gap-3">
                <Skeleton className="h-16" />
                <Skeleton className="h-16" />
              </div>
              <Skeleton className="h-10 w-64" />
            </div>
          ) : user ? (
            <UserLabelsSection
              userId={user.id}
              userName={user.full_name}
              userAddress={{
                full_name: user.full_name,
                address_line1: user.address_line1,
                address_line2: user.address_line2 || null,
                city: user.city,
                state: user.state,
                zip: user.zip,
                phone: user.phone,
              }}
              boxes={boxes.map((b) => ({
                id: b.id,
                box_number: b.box_number,
                name: b.name,
                shipping_label_url: b.shipping_label_url,
                tracking_number: b.tracking_number,
                easypost_tracking_url: b.easypost_tracking_url,
                label_generated_at: b.label_generated_at,
              }))}
              onLabelsGenerated={fetchUserBoxes}
            />
          ) : null}
        </CardContent>
      </Card>

      {/* Subscription — shows once user data is loaded */}
      {loadingUser ? (
        <CardSkeleton className="mt-6" />
      ) : user ? (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-4 w-4" /> Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Monthly Revenue:</span>
                <p className="font-medium text-lg">{formatCurrency(user.monthly_revenue)}</p>
              </div>
              {user.months_stored !== null && (
                <div>
                  <span className="text-muted-foreground">Months Stored:</span>
                  <p className="font-medium text-lg">{user.months_stored}</p>
                </div>
              )}
              {user.billing_start_date && (
                <div>
                  <span className="text-muted-foreground">Billing Start:</span>
                  <p className="font-medium">{format(new Date(user.billing_start_date), "MMM d, yyyy")}</p>
                </div>
              )}
              {user.signup_source && (
                <div>
                  <span className="text-muted-foreground">Signup Source:</span>
                  <p><Badge variant="outline">{user.signup_source}</Badge></p>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground block mb-1">Box Slots Purchased</label>
                  <Input type="number" min="0" value={boxSlotsPurchased} onChange={(e) => setBoxSlotsPurchased(e.target.value)} placeholder="0" className="text-sm" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground block mb-1">Billing Start Date</label>
                  <Input type="date" value={billingStartDate} onChange={(e) => setBillingStartDate(e.target.value)} className="text-sm" />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground block mb-1">Stripe Customer ID</label>
                <div className="flex gap-2">
                  <Input value={stripeCustomerId} onChange={(e) => setStripeCustomerId(e.target.value)} placeholder="cus_..." className="font-mono text-sm" />
                  {stripeCustomerId && (
                    <Button size="icon" variant="outline" onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripeCustomerId}`, "_blank")} title="View in Stripe">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground block mb-1">Stripe Subscription ID</label>
                <div className="flex gap-2">
                  <Input value={stripeSubscriptionId} onChange={(e) => setStripeSubscriptionId(e.target.value)} placeholder="sub_..." className="font-mono text-sm" />
                  {stripeSubscriptionId && (
                    <Button size="icon" variant="outline" onClick={() => window.open(`https://dashboard.stripe.com/subscriptions/${stripeSubscriptionId}`, "_blank")} title="View in Stripe">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {hasProfileChanges && (
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full">
                  {savingProfile ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-2" /> Save Changes</>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Boxes List — loads independently */}
      {loadingBoxes ? (
        <BoxListSkeleton />
      ) : (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="h-4 w-4" /> Boxes ({boxes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {boxes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No boxes yet</p>
            ) : (
              <div className="space-y-2">
                {boxes.map((box) => (
                  <div key={box.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{box.name}</p>
                      <p className="text-xs text-muted-foreground">#{box.box_number}</p>
                      {box.storage_start_date && (
                        <p className="text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {format(new Date(box.storage_start_date), "MMM d, yyyy")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {getLocationBadge(box.location_status)}
                      <Button size="sm" variant="ghost" onClick={() => { setEditingBox(box); setEditDialogOpen(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AdminBoxEditDialog box={editingBox} open={editDialogOpen} onOpenChange={setEditDialogOpen} onSaved={fetchUserBoxes} />
    </div>
  );
};

export default AdminUserDetail;
